import type { LegalSection } from './legalMeta';
import { LEGAL_META } from './legalMeta';

export const deleteYourDataTitle = 'Delete Your Data';

export const deleteYourDataSections: LegalSection[] = [
  {
    id: 'overview',
    title: `About ${LEGAL_META.brandName}`,
    paragraphs: [
      `This page explains how to delete some or all of your personal data in ${LEGAL_META.brandName} without closing your account, or how to delete your entire account if you prefer.`,
      'You can use the in-app steps below while signed in. If you cannot access the App, use the email option in the last section.',
    ],
  },
  {
    id: 'delete-posts',
    title: 'Delete a Post',
    paragraphs: [
      'Open the post you created → tap the options menu (⋯) → Delete post → confirm.',
      'This permanently removes that post’s photos, caption, place tag, and related comments on that post. Your account stays active.',
    ],
  },
  {
    id: 'delete-comments',
    title: 'Delete a Comment',
    paragraphs: [
      'Open the post → find your comment → tap the options menu → Delete comment → confirm.',
      'This permanently removes that comment. Your account and other content stay active.',
    ],
  },
  {
    id: 'edit-profile',
    title: 'Update or Remove Profile Information',
    paragraphs: [
      'Open Profile → Edit profile to change or clear information such as your bio, profile photo, or cover photo.',
      'Saving changes updates what is stored on our servers. Removing a photo deletes that image from your profile.',
    ],
  },
  {
    id: 'location-permission',
    title: 'Stop Sharing Location',
    paragraphs: [
      'You can turn off location access for the App in your device settings at any time.',
      'We use location only when you choose features that need it (for example, tagging a place or nearby search). Turning off permission stops new location collection from your device.',
    ],
  },
  {
    id: 'what-deleted',
    title: 'What Is Deleted',
    paragraphs: ['When you delete content or profile fields as described above, we delete or de-identify:'],
    listItems: [
      'The specific posts, photos, captions, and comments you delete.',
      'Profile fields you clear or replace (such as bio or avatar).',
      'Associated media stored for deleted posts or removed profile photos.',
    ],
  },
  {
    id: 'retention',
    title: 'What May Be Kept',
    paragraphs: [
      'Limited backups may persist for a short period for security and disaster recovery, then are purged according to our systems.',
      'We may retain minimal records where required by law, fraud prevention, or to resolve disputes.',
      'Content you shared externally (for example via share to another app) is not controlled by us after sharing.',
      'Account credentials, email, username, and other data not covered by the steps above remain until you delete your account or request removal by email.',
    ],
  },
  {
    id: 'processing-time',
    title: 'Processing Time',
    paragraphs: [
      'In-app deletions are processed as soon as practicable, usually within 30 days. Email requests are handled within a reasonable time after we verify your identity.',
    ],
  },
  {
    id: 'email-request',
    title: 'Cannot Access the App or Need Other Data Removed',
    paragraphs: [
      `Email ${LEGAL_META.contactEmail} from the address associated with your account with the subject “Data deletion request.”`,
      'Describe what you want deleted (for example, specific posts, profile fields, or other personal data). We will verify ownership and process your request.',
    ],
  },
  {
    id: 'delete-account',
    title: 'Delete Your Entire Account',
    paragraphs: [
      `To permanently delete your account and all associated data, open ${LEGAL_META.brandName} → Settings → Account → Delete account, or see our Account Deletion page on this site.`,
    ],
  },
];
